import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import styles from './AdminPage.module.css';
import LoginPage from './LoginPage';
import ScheduleArea from './ScheduleArea'; // Import schedule area component with tabs
import { onAuthStateChanged, logout, getCurrentUser } from '../api/authService'; // Import from new auth service
import AdminDonationsPage from './AdminDonationsPage'; // Import actual component
import AdminFamiliesPage from './AdminFamiliesPage'; // Import actual component
import KioskSettings from './KioskSettings'; // Import kiosk settings component
import ManageArea from './ManageArea'; // Import manage area component

// Placeholder component for new admin sections
const AdminDashboard = () => <h2>Admin Dashboard</h2>; // Default view for /admin

const AdminPage = () => {
  const [currentUser, setCurrentUser] = useState(getCurrentUser()); // Initialize with localStorage
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;
    
    // Set a timeout to stop loading after 5 seconds as a fallback
    const loadingTimeout = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 5000);
    
    const unsubscribe = onAuthStateChanged((user) => { // No need to pass auth instance
      if (isMounted) {
        clearTimeout(loadingTimeout);
        setCurrentUser(user);
        setLoading(false);
      }
      if (!user && location.pathname.startsWith('/admin')) {
        // If user logs out or session expires, and they are on an admin page (excluding login)
        // navigate('/admin/login', { replace: true }); // Or simply let the ProtectedRoute handle it
      }
    });

    // if (process.env.NODE_ENV === 'development' && currentUser) { // Call if in dev and user is potentially logged in
    //   testCall();
    // }

    return () => {
      isMounted = false;
      clearTimeout(loadingTimeout);
      unsubscribe(); // Cleanup subscription
    };
  }, [navigate, location.pathname]); // Removed currentUser from dependency array to prevent infinite loops

  const handleLogout = async () => {
    try {
      await logout();
      setCurrentUser(null); // Update state immediately
      navigate('/admin/login', { replace: true }); // Redirect to login after logout
    } catch (error) {
      console.error('Logout error:', error);
      // Handle logout error (e.g., show a notification)
    }
  };
  
  const handleLoginSuccess = () => {
    // getCurrentUser will now pick up from localStorage or auth.currentUser
    setCurrentUser(getCurrentUser()); 
    // Navigate to a default admin page or the page they were trying to access
    const from = location.state?.from?.pathname || '/admin';
    navigate(from, { replace: true });
  };

  if (loading) {
    return (
      <div className={styles.adminContainer}>
        <div className={styles.loadingScreen}>
          <div className={styles.loadingMessage}>Loading Authentication...</div>
        </div>
      </div>
    );
  }

  // The ProtectedRoute component will handle redirection if !currentUser for protected paths.
  // The /admin/login route has its own logic to redirect if currentUser exists.

  return (
    <div className={styles.adminContainer}>
      {currentUser && location.pathname !== '/admin/login' && (
        <header className={styles.adminHeader}>
          <h1>Beis Levi Yitzchok Admin</h1>
          <nav className={styles.adminNav}>
            <Link to="/admin" className={styles.navButton}>Dashboard</Link>
            <Link to="/admin/schedule/weekly" className={styles.navButton}>Schedule Editor</Link>
            <Link to="/admin/kiosk-settings" className={styles.navButton}>Kiosk Settings</Link>
            <Link to="/admin/donations" className={styles.navButton}>Donations</Link>
            <Link to="/admin/families" className={styles.navButton}>Families</Link>
            <Link to="/admin/manage/users" className={styles.navButton}>Manage</Link>
          </nav>
          <button className={styles.logoutButton} onClick={handleLogout}>
            Logout
          </button>
        </header>
      )}
      <main className={styles.adminContent}>
        <Routes>
          {/* Public route for login */}
          <Route path="login" element={!currentUser ? <LoginPage onLoginSuccess={handleLoginSuccess} /> : <Navigate to="/admin" replace />} />

          {/* Protected Routes */}
          <Route path="" element={<ProtectedRoute user={currentUser}><AdminDashboard /></ProtectedRoute>} />
          <Route path="schedule/*" element={<ProtectedRoute user={currentUser}><ScheduleArea /></ProtectedRoute>} />
          <Route path="kiosk-settings" element={<ProtectedRoute user={currentUser}><KioskSettings /></ProtectedRoute>} />
          <Route path="donations" element={<ProtectedRoute user={currentUser}><AdminDonationsPage /></ProtectedRoute>} />
          <Route path="families" element={<ProtectedRoute user={currentUser}><AdminFamiliesPage /></ProtectedRoute>} />
          <Route path="manage/*" element={<ProtectedRoute user={currentUser}><ManageArea /></ProtectedRoute>} />
          
          {/* Fallback for any other /admin/* route. 
              If logged in, redirect to admin dashboard. 
              If not, ProtectedRoute on dashboard will redirect to login.
              Or, if they somehow land on a non-existent /admin/something route while logged out,
              this will also push to login via ProtectedRoute.
          */}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// Component to protect routes
const ProtectedRoute = ({ user, children }) => {
  const location = useLocation();
  if (!user) {
    // Redirect them to the /admin/login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the adminL home page.
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  return children;
};

export default AdminPage;
